import React, {useCallback, useContext} from 'react';
import {useNavigate} from 'react-router-dom';

import {DisabledText} from 'src/components/profile-tab-structure';
import {Skeleton} from 'src/components/skeleton';
import {USER_TABS} from 'src/constants';
import {ProductAccessTermStatus} from 'src/modules/profile/user/components/product-access-term-status';
import {useUserSeatAssignments} from 'src/modules/profile/user/hooks/use-user-seat-assignments';
import {getSummarySeatAssignments} from 'src/modules/profile/user/tabs/products/seat-assignment-utils';
import {formatLicenseDate} from 'src/modules/profile/utils/formatLicenseDate';
import {SeatAssignmentStatus} from 'src/openapi/admin/models/SeatAssignmentStatus';

import {FlatButton} from '@pb/kit-component-button';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import {LocaleContext} from '@pb/kit-context-locale';

type Props = {userId: number};

export const ProductAccessSection: React.FC<Props> = ({userId}) => {
    const navigate = useNavigate();
    const {locale} = useContext(LocaleContext);
    const {assignments, error} = useUserSeatAssignments(userId, SeatAssignmentStatus.ACTIVE, 1, 5);
    const visibleAssignments = getSummarySeatAssignments(assignments || []);
    const onEdit = useCallback(() => navigate(`/profile/user/${userId}/${USER_TABS.PRODUCTS.name}`), [navigate, userId]);

    return (
        <Skeleton shown={assignments === null && !error} view={<FormSection headline="Product Access"/>}>
            <FormSection
                headline="Product Access"
                info={headline => (
                    <Item grow={1}>
                        <Group justify="space-between" alignItems="center">
                            {headline}
                            <FlatButton onClick={onEdit}><b>Edit</b></FlatButton>
                        </Group>
                    </Item>
                )}>
                {error ? <DisabledText>Unable to load product access.</DisabledText> : null}
                {!error && assignments?.length === 0 ? (
                    <DisabledText>This user has no active seat assignments</DisabledText>
                ) : null}
                <Group direction="v" gap="s">
                    {visibleAssignments.map(assignment => (
                        <Group key={assignment.id} direction="h" gap="xs" display="flex" justify="space-between">
                            <Group gap="s" alignItems="center">
                                <strong>{assignment.product.name}</strong>
                                <ProductAccessTermStatus status={assignment.subscriptionLifecycleStatus}/>
                            </Group>
                            <span>
                                {formatLicenseDate(locale.dateFormat, assignment.subscriptionStartDate)} →
                                {' '}{formatLicenseDate(locale.dateFormat, assignment.subscriptionEndDate)}
                            </span>
                        </Group>
                    ))}
                </Group>
            </FormSection>
        </Skeleton>
    );
};
